import { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'motion/react';
import { ArrowLeft, Crown, Users, MapPin, BookmarkPlus, Tag, Calendar, Bell, BellOff, Trash2, User } from 'lucide-react';
import { API_BASE } from '../utils/supabase/info';
import { toast } from '../utils/toast';
import { handleApiError } from '../utils/api-error-handler';
import { requestPushNotificationPermission } from '../utils/pushNotifications';
import { ConfirmDialog } from './ConfirmDialog';
import { SearchLoader } from './SearchLoader';
import { devLog, devError, devWarn } from '../utils/logger';
import { canUploadVideo } from '../utils/accessPolicy';
import { triggerAndroidHaptic } from '../utils/haptics';
import { PageShell } from './layout/PageShell';


interface SavedSearch {
  id: string;
  name: string;
  searchTerms: string[];
  counties: string[];
  notificationsEnabled: boolean;
  matchCount: number;
  createdAt: string;
  lastNotifiedAt: string | null;
}

interface SavedSearchesPageProps {
  accessToken: string;
  onBack: () => void;
  onGoToSearch: (savedSearch?: Pick<SavedSearch, 'searchTerms' | 'counties' | 'name'>) => void;
  onViewProfile: (userId: string) => void;
  userProfile?: any;
  initialSearchId?: string | null; // For deep linking from push notifications
  onClearInitialSearch?: () => void; // Callback to clear the initial search ID after handling
  isVisible?: boolean;
  /** When true (desktop modal), hide the sub-page header; modal chrome shows back button. */
  hideHeader?: boolean;
  onNavigateToSubscription?: () => void;
}

export function SavedSearchesPage({ accessToken, onBack, onGoToSearch, onViewProfile, userProfile, initialSearchId, onClearInitialSearch, isVisible = true, hideHeader = false, onNavigateToSubscription }: SavedSearchesPageProps) {
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSearchId, setSelectedSearchId] = useState<string | null>(null);
  const [matches, setMatches] = useState<any[]>([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [searchToDelete, setSearchToDelete] = useState<{ id: string; name: string } | null>(null);
  const [deletingSearch, setDeletingSearch] = useState(false);
  /** Set when API returns 403 requiresUpgrade – use API as source of truth, not userProfile */
  const [accessDenied, setAccessDenied] = useState(false);

  // iOS PWA: JS-based sticky sub-page header (position:sticky broken in standalone)
  const stickySentinelRef = useRef<HTMLDivElement>(null);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const stickyPlaceholderRef = useRef<HTMLDivElement>(null);
  const subPageContentRef = useRef<HTMLDivElement>(null);

  // iOS PWA: when page becomes visible, scroll .sub-page-content to top (persistent mount keeps scroll position)
  useEffect(() => {
    if (!isVisible) return;
    const isIosPwa = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-pwa');
    if (!isIosPwa) return;
    const el = subPageContentRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      el.scrollTo(0, 0);
    });
    return () => cancelAnimationFrame(id);
  }, [isVisible]);

  useEffect(() => {
    const isIosPwa = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-pwa');
    if (!isIosPwa) return;
    const sentinel = stickySentinelRef.current;
    const bar = stickyHeaderRef.current;
    const placeholder = stickyPlaceholderRef.current;
    if (!sentinel || !bar || !placeholder) return;
    const header = document.querySelector('.pwa-nav-top');
    const headerH = header ? header.getBoundingClientRect().height : 56;
    let isStuck = false;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const shouldStick = !entry.isIntersecting;
        if (shouldStick === isStuck) return;
        isStuck = shouldStick;
        if (shouldStick) {
          const mb = parseFloat(window.getComputedStyle(bar).marginBottom) || 0;
          placeholder.style.height = `${bar.offsetHeight + mb}px`;
          bar.classList.add('is-stuck');
        } else {
          bar.classList.remove('is-stuck');
          placeholder.style.height = '0px';
        }
      },
      { rootMargin: `-${Math.ceil(headerH)}px 0px 0px 0px`, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Define viewMatches early so it can be used in useEffect
  const viewMatches = useCallback(async (searchId: string) => {
    setSelectedSearchId(searchId);
    setLoadingMatches(true);
    try {
      const response = await fetch(
        `${API_BASE}/saved-searches/${searchId}/matches`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch matches');
      }

      const data = await response.json();
      setMatches(data.matches || []);
    } catch (error) {
      devError('Error fetching matches:', error);
      toast.error('Noe gikk galt', 'Kunne ikke hente matches');
    } finally {
      setLoadingMatches(false);
    }
  }, [accessToken]);

  // Refetch when accessToken changes (e.g. after re-login) so list is not empty
  useEffect(() => {
    if (!accessToken) return;
    if (isVisible) {
      fetchSavedSearches();
    }
  }, [accessToken, isVisible]);

  useEffect(() => {
    if (!isVisible) return;
    const handleSavedSearchesUpdated = () => {
      void fetchSavedSearches();
      if (selectedSearchId) {
        void viewMatches(selectedSearchId);
      }
    };
    window.addEventListener('talentfunn:saved-searches-updated', handleSavedSearchesUpdated);
    return () => {
      window.removeEventListener('talentfunn:saved-searches-updated', handleSavedSearchesUpdated);
    };
  }, [isVisible, accessToken, selectedSearchId, viewMatches]);

  useEffect(() => {
    if (!isVisible) return;

    const refreshSavedSearches = () => {
      void fetchSavedSearches();
      if (selectedSearchId) {
        void viewMatches(selectedSearchId);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshSavedSearches();
      }
    };

    window.addEventListener('focus', refreshSavedSearches);
    window.addEventListener('online', refreshSavedSearches);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', refreshSavedSearches);
      window.removeEventListener('online', refreshSavedSearches);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isVisible, accessToken, selectedSearchId, viewMatches]);

  // Handle deep link from push notification
  useEffect(() => {
    if (initialSearchId && savedSearches.length > 0) {
      devLog('🔗 Auto-opening saved search matches:', initialSearchId);
      // Verify that the search exists in the list
      const searchExists = savedSearches.some(s => s.id === initialSearchId);
      if (searchExists) {
        viewMatches(initialSearchId);
      } else {
        devWarn('⚠️ Initial search ID not found in saved searches:', initialSearchId);
      }
      // Clear the initial search ID after handling
      if (onClearInitialSearch) {
        onClearInitialSearch();
      }
    }
  }, [initialSearchId, savedSearches, viewMatches, onClearInitialSearch]);

  const fetchSavedSearches = async () => {
    try {
      setAccessDenied(false);
      const response = await fetch(
        `${API_BASE}/saved-searches`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 403 && errorData.requiresUpgrade) {
          setAccessDenied(true);
        } else {
          handleApiError(errorData, () => {
            onNavigateToSubscription?.();
          });
        }
        setLoading(false);
        return;
      }

      const data = await response.json();
      setSavedSearches(data.savedSearches || []);
    } catch (error) {
      devError('Error fetching saved searches:', error);
      toast.error('Noe gikk galt', 'Kunne ikke hente lagrede søk');
    } finally {
      setLoading(false);
    }
  };

  const toggleNotifications = async (searchId: string, currentState: boolean) => {
    try {
      // If enabling notifications, request push permission first
      if (!currentState) {
        const permissionGranted = await requestPushNotificationPermission(accessToken);
        
        if (!permissionGranted) {
          toast.warning('Varsler blokkert', 'Vennligst aktiver push-varsler i nettleserinnstillingene');
          return;
        }
      }

      const response = await fetch(
        `${API_BASE}/saved-searches/${searchId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`
          },
          body: JSON.stringify({
            notificationsEnabled: !currentState
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update notifications');
      }

      // Update local state
      setSavedSearches(prev =>
        prev.map(search =>
          search.id === searchId
            ? { ...search, notificationsEnabled: !currentState }
            : search
        )
      );
      window.dispatchEvent(new Event('talentfunn:saved-searches-updated'));

      toast.success(
        !currentState ? 'Varsler aktivert' : 'Varsler deaktivert',
        !currentState
          ? 'Du vil motta varsler om nye matches'
          : 'Du vil ikke lenger motta varsler'
      );
    } catch (error) {
      devError('Error toggling notifications:', error);
      toast.error('Noe gikk galt', 'Kunne ikke oppdatere varsler');
    }
  };

  const deleteSearch = async () => {
    if (!searchToDelete) return;

    setDeletingSearch(true);
    try {
      const response = await fetch(
        `${API_BASE}/saved-searches/${searchToDelete.id}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to delete search');
      }

      toast.success('Søk slettet', `"${searchToDelete.name}" er fjernet`);

      // Update local state
      setSavedSearches(prev => prev.filter(s => s.id !== searchToDelete.id));
      
      // Close matches view if deleted search was selected
      if (selectedSearchId === searchToDelete.id) {
        setSelectedSearchId(null);
        setMatches([]);
      }

      // Close dialog and reset
      setShowDeleteConfirm(false);
      setSearchToDelete(null);
      window.dispatchEvent(new Event('talentfunn:saved-searches-updated'));
    } catch (error) {
      devError('Error deleting search:', error);
      toast.error('Noe gikk galt', 'Kunne ikke slette søk');
    } finally {
      setDeletingSearch(false);
    }
  };

  // Avatar colors for talent cards
  const avatarColors = [
    'from-purple-500 to-pink-500',
    'from-blue-500 to-cyan-500',
    'from-orange-500 to-red-500',
    'from-green-500 to-emerald-500',
  ];
  
  const getAvatarColor = (name: string) => {
    const index = name?.charCodeAt(0) % avatarColors.length || 0;
    return avatarColors[index];
  };

  const pageHeader = hideHeader ? null : (
    <div ref={stickyHeaderRef} className="sub-page-header bg-white border-b border-gray-200 px-4 pt-4 pb-3 flex items-center gap-3 flex-shrink-0 sticky top-0 z-20">
      <button
        onClick={selectedSearchId ? () => { triggerAndroidHaptic('light'); setSelectedSearchId(null); setMatches([]); } : () => { triggerAndroidHaptic('light'); onBack(); }}
        className="p-2 hover:bg-gray-100 rounded-lg transition-all duration-150 active:scale-95"
        aria-label={selectedSearchId ? 'Tilbake til lagrede søk' : 'Tilbake'}
      >
        <ArrowLeft className="w-5 h-5" aria-hidden />
      </button>
      <div className="flex-1 flex items-center gap-3">
        <BookmarkPlus className="w-8 h-8 text-purple-600 flex-shrink-0" />
        <div>
          <h2 className="font-semibold">
            {selectedSearchId ? (savedSearches.find(s => s.id === selectedSearchId)?.name ?? 'Matches') : 'Mine lagrede søk'}
          </h2>
          <p className="text-sm text-gray-600">
            {selectedSearchId
              ? `${matches.length} ${matches.length === 1 ? 'match' : 'matches'}`
              : `${savedSearches.length}/3 lagrede søk`}
          </p>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen xl:min-h-0 xl:h-full flex flex-col bg-white xl:bg-gradient-to-br xl:from-purple-50 xl:via-pink-50 xl:to-blue-50 xl:overflow-hidden">
        {!hideHeader && <div ref={stickySentinelRef} className="h-0 w-full" aria-hidden="true" />}
        {pageHeader}
        {!hideHeader && <div ref={stickyPlaceholderRef} style={{ height: 0 }} aria-hidden="true" />}
        <div ref={subPageContentRef} className="sub-page-content flex-1 xl:min-h-0 xl:overflow-y-auto">
          <PageShell className="px-4 py-4 xl:max-w-4xl xl:mx-auto xl:py-6">
            {hideHeader && (
              <div className="flex items-center gap-3 pb-4 mb-4 border-b border-gray-200">
                <BookmarkPlus className="w-8 h-8 text-purple-600 flex-shrink-0" />
                <div>
                  <h2 className="font-semibold text-gray-900">Lagrede søk</h2>
                  <p className="text-sm text-gray-600">Laster...</p>
                </div>
              </div>
            )}
            <div className="text-center py-12">
              <SearchLoader text="Laster lagrede søk..." fullPage />
            </div>
          </PageShell>
        </div>
      </div>
    );
  }

  // Show upgrade block only when API explicitly denied access (403 requiresUpgrade)
  if (accessDenied) {
    return (
      <div className="min-h-screen xl:min-h-0 xl:h-full flex flex-col bg-white xl:bg-gradient-to-br xl:from-purple-50 xl:via-pink-50 xl:to-blue-50 xl:overflow-hidden">
        {!hideHeader && <div ref={stickySentinelRef} className="h-0 w-full" aria-hidden="true" />}
        {pageHeader}
        {!hideHeader && <div ref={stickyPlaceholderRef} style={{ height: 0 }} aria-hidden="true" />}
        <div ref={subPageContentRef} className="sub-page-content flex-1 xl:min-h-0 xl:overflow-y-auto">
          <PageShell className="px-4 py-4 xl:max-w-4xl xl:mx-auto xl:py-6">
            {hideHeader && (
              <div className="flex items-center gap-3 pb-4 mb-4 border-b border-gray-200">
                <BookmarkPlus className="w-8 h-8 text-purple-600 flex-shrink-0" />
                <div>
                  <h2 className="font-semibold text-gray-900">Lagrede søk</h2>
                  <p className="text-sm text-gray-600">Krever Pluss</p>
                </div>
              </div>
            )}
            <div className="bg-white p-8 text-center xl:rounded-3xl xl:shadow-lg xl:p-12 xl:border-2 xl:border-purple-100">
              <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center mx-auto mb-6">
                <Crown className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl text-gray-900 mb-4">Lagrede søk krever Pluss</h2>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                Med Arbeidsgiver Pluss kan du lagre inntil 3 søk og motta varsler automatisk når nye talenter matcher dine kriterier.
              </p>
              <button
                onClick={() => {
                  triggerAndroidHaptic('light');
                  onNavigateToSubscription?.();
                }}
                className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full hover:shadow-lg transition-all duration-150 active:scale-[0.98] inline-flex items-center gap-2"
              >
                <Crown className="w-5 h-5" />
                Oppgrader til Pluss
              </button>
            </div>
          </PageShell>
        </div>
      </div>
    );
  }

  // Show matches view
  if (selectedSearchId) {
    const selectedSearch = savedSearches.find((s) => s.id === selectedSearchId);
    return (
      <div className="min-h-screen xl:min-h-0 xl:h-full flex flex-col bg-white xl:bg-gradient-to-br xl:from-purple-50 xl:via-pink-50 xl:to-blue-50 xl:overflow-hidden">
        {!hideHeader && <div ref={stickySentinelRef} className="h-0 w-full" aria-hidden="true" />}
        {pageHeader}
        {!hideHeader && <div ref={stickyPlaceholderRef} style={{ height: 0 }} aria-hidden="true" />}
        <div ref={subPageContentRef} className="sub-page-content flex-1 xl:min-h-0 xl:overflow-y-auto">
          <PageShell className="px-4 py-4 xl:max-w-4xl xl:mx-auto xl:py-6">
            {hideHeader && (
              <div className="flex items-center gap-3 pb-4 mb-4 border-b border-gray-200">
                <BookmarkPlus className="w-8 h-8 text-purple-600 flex-shrink-0" />
                <div>
                  <h2 className="font-semibold text-gray-900">{selectedSearch?.name ?? 'Matches'}</h2>
                  <p className="text-sm text-gray-600">
                    {loadingMatches ? '...' : `${matches.length} ${matches.length === 1 ? 'match' : 'matches'}`}
                  </p>
                </div>
              </div>
            )}
            {loadingMatches ? (
              <div className="flex items-center justify-center py-12">
                <SearchLoader text="Laster treff..." fullPage />
              </div>
            ) : matches.length === 0 ? (
              <div className="bg-white p-8 text-center xl:rounded-3xl xl:shadow-lg xl:p-12 xl:border-2 xl:border-purple-100">
                <Users className="w-20 h-20 text-gray-300 mx-auto mb-6" />
                <h2 className="text-2xl text-gray-600 mb-4">Ingen treff ennå</h2>
                <p className="text-gray-500">
                  Vi varsler deg når nye talenter matcher dette søket
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-4 xl:gap-6">
                {matches.map((talent) => (
                  <div
                    key={talent.id}
                    className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200 xl:rounded-3xl xl:shadow-lg xl:border-2 xl:border-purple-100 hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5 transform-gpu group cursor-pointer"
                    onClick={() => {
                      triggerAndroidHaptic('light');
                      onViewProfile(talent.id);
                    }}
                  >
                    {/* Cover gradient */}
                    <div className={`h-24 bg-gradient-to-r ${getAvatarColor(talent.name || 'A')}`} />
                    
                    <div className="px-6 pb-6 -mt-12">
                      {/* Avatar */}
                      {talent.profileImage ? (
                        <img
                          src={talent.profileImage}
                          alt={talent.name}
                          className="w-24 h-24 rounded-3xl border-4 border-white shadow-xl mb-4 object-cover"
                        />
                      ) : (
                        <div className={`w-24 h-24 bg-gradient-to-br ${getAvatarColor(talent.name || 'A')} rounded-3xl flex items-center justify-center border-4 border-white shadow-xl mb-4`}>
                          {talent.name?.charAt(0) ? (
                            <span className="text-4xl text-white">
                              {talent.name.charAt(0).toUpperCase()}
                            </span>
                          ) : (
                            <User className="w-12 h-12 text-white" />
                          )}
                        </div>
                      )}

                      {/* Name and badge */}
                      <div className="flex items-center gap-2 mb-3">
                        <h3 className="text-xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1">
                          {talent.name || 'Ukjent'}
                        </h3>
                        {canUploadVideo(talent) && (
                          <div className="flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full">
                            <Crown className="w-3 h-3 text-white" />
                            <span className="text-white text-xs">PLUSS</span>
                          </div>
                        )}
                      </div>

                      {/* Location */}
                      {talent.location && (
                        <div className="flex items-center gap-2 text-gray-600 mb-3">
                          <MapPin className="w-4 h-4" />
                          <span className="text-sm">{talent.location}</span>
                        </div>
                      )}

                      {/* Bio */}
                      {talent.bio && (
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                          {talent.bio}
                        </p>
                      )}

                      {/* Portfolio count */}
                      {talent.portfolioCount > 0 && (
                        <p className="text-xs text-purple-600">
                          {talent.portfolioCount} {talent.portfolioCount === 1 ? 'post' : 'posts'} i portefølje
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </PageShell>
        </div>
      </div>
    );
  }

  // Main saved searches list
  return (
    <div className="min-h-screen xl:min-h-0 xl:h-full flex flex-col bg-white xl:bg-gradient-to-br xl:from-purple-50 xl:via-pink-50 xl:to-blue-50 xl:overflow-hidden">
      {!hideHeader && <div ref={stickySentinelRef} className="h-0 w-full" aria-hidden="true" />}
      {pageHeader}
      {!hideHeader && <div ref={stickyPlaceholderRef} style={{ height: 0 }} aria-hidden="true" />}
      <div ref={subPageContentRef} className="sub-page-content flex-1 xl:min-h-0 xl:overflow-y-auto">
        <PageShell className="px-4 py-4 xl:max-w-4xl xl:mx-auto xl:py-6">
          {hideHeader && (
            <div className="flex items-center gap-3 pb-4 mb-4 border-b border-gray-200">
              <BookmarkPlus className="w-8 h-8 text-purple-600 flex-shrink-0" />
              <div>
                <h2 className="font-semibold text-gray-900">Mine lagrede søk</h2>
                <p className="text-sm text-gray-600">{savedSearches.length}/3 lagrede søk</p>
              </div>
            </div>
          )}
          {/* Saved Searches */}
          <div className="pb-8">
              {savedSearches.length === 0 ? (
                <div className="bg-white p-8 text-center xl:rounded-3xl xl:shadow-lg xl:p-12 xl:border-2 xl:border-purple-100">
                  <BookmarkPlus className="w-20 h-20 text-gray-300 mx-auto mb-6" />
                  <h2 className="text-2xl text-gray-600 mb-4">Ingen lagrede søk ennå</h2>
                  <p className="text-gray-500 mb-8">
                    Gå til søkesiden, søk etter talenter, og klikk på bookmark-ikonet for å lagre søket.
                  </p>
                  <button
                    onClick={() => {
                      triggerAndroidHaptic('light');
                      onGoToSearch();
                    }}
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all duration-150 active:scale-[0.98] font-semibold"
                  >
                    Gå til søk
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 xl:gap-6">
                  {savedSearches.map((search) => (
                    <div
                      key={search.id}
                      className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200 xl:rounded-3xl xl:shadow-lg xl:border-2 xl:border-purple-100 hover:shadow-xl transition-all duration-200"
                    >
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div 
                            className="flex-1 cursor-pointer"
                            onClick={() => {
                              triggerAndroidHaptic('light');
                              viewMatches(search.id);
                            }}
                          >
                            <div className="flex items-center gap-3 mb-3">
                              <h3 className="text-xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1">
                                {search.name}
                              </h3>
                              {/* Match count badge */}
                              {search.matchCount > 0 && (
                                <span className="px-3 py-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full text-sm flex-shrink-0">
                                  {search.matchCount} {search.matchCount === 1 ? 'match' : 'matches'}
                                </span>
                              )}
                            </div>
                            
                            {/* Search terms */}
                            {search.searchTerms.length > 0 && (
                              <div className="flex flex-wrap gap-2 mb-3">
                                {search.searchTerms.map((term, index) => (
                                  <span
                                    key={index}
                                    className="px-3 py-1 bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 rounded-lg text-xs border border-purple-200 inline-flex items-center gap-1"
                                  >
                                    <Tag className="w-3 h-3" />
                                    {term}
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* Counties */}
                            {search.counties.length > 0 && (
                              <div className="flex flex-wrap gap-2 mb-3">
                                {search.counties.map((county, index) => (
                                  <span
                                    key={index}
                                    className="px-3 py-1 bg-gradient-to-r from-blue-50 to-cyan-50 text-blue-700 rounded-lg text-xs border border-blue-200 inline-flex items-center gap-1"
                                  >
                                    <MapPin className="w-3 h-3" />
                                    {county}
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* Last updated */}
                            {search.lastNotifiedAt && (
                              <p className="text-xs text-gray-500 flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                Sist oppdatert: {new Date(search.lastNotifiedAt).toLocaleDateString('nb-NO')}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                          {/* Notifications toggle */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              triggerAndroidHaptic('light');
                              toggleNotifications(search.id, search.notificationsEnabled);
                            }}
                            className={`flex items-center gap-2 text-sm px-4 py-2 rounded-full transition-all duration-150 active:scale-[0.98] ${
                              search.notificationsEnabled
                                ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {search.notificationsEnabled ? (
                              <>
                                <Bell className="w-4 h-4" />
                                <span>Varsler på</span>
                              </>
                            ) : (
                              <>
                                <BellOff className="w-4 h-4" />
                                <span>Varsler av</span>
                              </>
                            )}
                          </button>

                          {/* Action buttons */}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                triggerAndroidHaptic('light');
                                onGoToSearch(search);
                              }}
                              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full hover:shadow-md transition-all duration-150 active:scale-[0.98] text-sm inline-flex items-center gap-2"
                              title={`Bruk "${search.name}" i søk`}
                            >
                              Gå til søk
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                triggerAndroidHaptic('light');
                                setSearchToDelete({ id: search.id, name: search.name });
                                setShowDeleteConfirm(true);
                              }}
                              className="px-4 py-2 bg-red-500 text-white rounded-full hover:shadow-md transition-all duration-150 active:scale-[0.98] text-sm inline-flex items-center gap-2"
                              title="Slett søk"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </PageShell>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          triggerAndroidHaptic('light');
          setShowDeleteConfirm(false);
          setSearchToDelete(null);
        }}
        onConfirm={() => {
          triggerAndroidHaptic('medium');
          deleteSearch();
        }}
        title="Slett søk?"
        message={searchToDelete ? `Er du sikker på at du vil slette "${searchToDelete.name}"?` : ''}
        confirmText="Slett"
        cancelText="Avbryt"
        variant="danger"
        loading={deletingSearch}
        loadingText="Sletter..."
      />
    </div>
  );
}
